import { Megaphone } from 'lucide-react';
import ServiceStoryPage from '../../components/ServiceStoryPage';

const color = 'hsl(243 75% 59%)';

export const seo = {
  title: 'Marketing',
  tagline: 'Advertise on the platforms that matter',
  metaDesc: 'PPC, Google Ads, Facebook and Instagram Ads set up and managed for UK small businesses. Campaigns built around real goals, not vanity metrics.',
  slug: 'marketing',
  whatWeDoItems: [
    { heading: 'Social posts written for you', body: 'Engaging, on-brand posts that speak to your customers — consistent content that builds your presence without you having to think about it.' },
    { heading: 'Content ideas & planning', body: 'A simple plan built around what your customers are actually searching for, so your feed stays relevant and easy to find.' },
    { heading: 'Uploaded & scheduled', body: 'We upload and schedule everything across your agreed platforms, so your social presence stays active without any effort on your end.' },
    { heading: 'Google Business Profile kept sharp', body: 'Regular posts, offers, photos and answers on your Google profile, so you stay visible in local search and give people a reason to choose you.' },
    { heading: 'Google Ads that pay their way', body: 'Search and display campaigns that put you in front of people actively looking for what you offer — keywords, ad copy, bidding and ongoing tuning, all handled.' },
    { heading: 'Facebook & Instagram ads', body: 'Targeted paid campaigns designed to stop the scroll and turn the right people into real enquiries — from first glance to getting in touch.' },
    { heading: 'Smart retargeting', body: 'We gently bring back people who already showed interest, so warm prospects don’t just drift away and forget about you.' },
    { heading: 'Honest advice on where to spend', body: 'Straight-talking guidance on where your budget and effort will get the best return — no upselling, no jargon.' },
    { heading: 'Plain-English reporting', body: 'Regular summaries of what’s working, what isn’t, and what we’re doing about it. No confusing dashboards to decode.' },
  ],
};

export default function MarketingPage() {
  return (
    <ServiceStoryPage
      tagline={seo.tagline}
      Icon={Megaphone}
      color={color}
      ctaText="Book a free call"
      heroHeadline="Get found by customers who are ready to buy"
      heroSub="We handle your marketing end to end — writing content, planning campaigns and managing your ad spend — all built around real business goals, not follower counts. More of the right people, less wasted money."
      problem={{
        heading: 'You know you should be marketing — but where do you even start?',
        body: [
          'You post when you remember, boost the odd thing, and mostly feel like you’re shouting into the void. There’s never enough time, and you’re never quite sure any of it is working.',
          'Maybe you’ve tried ads before and watched the money disappear with nothing to show for it. Meanwhile your competitors keep popping up above you, and you’re left wondering what they know that you don’t.',
        ],
      }}
      whatYouGetIntro="A complete marketing setup — content, campaigns and ads — all managed for you and pointed squarely at winning you customers."
      whatYouGet={seo.whatWeDoItems}
      howItWorks={[
        { title: 'A free assessment', body: 'We look at where you are now — your goals, your budget, and where your best customers actually spend their time.' },
        { title: 'A plan around your goals', body: 'We map out the content and campaigns that fit your business, so every pound has a clear job to do.' },
        { title: 'We launch and manage', body: 'We create the content, set up the ads and keep everything running — you get on with the day job.' },
        { title: 'We review and improve', body: 'Every month we check what’s working, cut what isn’t, and keep pushing your results in the right direction.' },
      ]}
      proofHeading="Marketing that brought in real money"
      proofSub="For Eclipse Paving we set up and manage Google Ads alongside their site and SEO — helping generate over £70k in work from ads, with campaigns tuned around genuine leads rather than vanity metrics."
      pricing={{
        price: 'Priced after a free assessment',
        caption: 'Every brief is different, so we scope your marketing around your goals and budget after a free assessment.',
      }}
      finalHeading="Ready to reach the right customers?"
      finalSub="Book a free assessment and we’ll show you exactly where your marketing budget will work hardest — with a clear plan and no jargon."
    />
  );
}
